import { MoreHoriz, ContentCopy, Link } from "@mui/icons-material";
import {
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButtonProps,
  Typography,
  Box,
} from "@mui/material";
import React, { useState } from "react";
import { Check as CheckIcon, Email as EmailIcon } from "@mui/icons-material";
import { copyToClipboard } from "~/lib/clipboard";

type ShareFindingProps = {
  IconButtonProps?: IconButtonProps;
  title?: string;
  additionalMenuItems?: React.ReactNode;
};

export function TriageBarActionButtons({
  IconButtonProps,
  title,
  additionalMenuItems,
}: ShareFindingProps) {
  const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [copyMessage, setCopyMessage] = useState<string>("");

  const shareOpen = Boolean(shareAnchorEl);

  const shareLink = window.location.href;
  const shareEmailLink = `mailto:hello@mondoo.com?subject=${encodeURIComponent("Take a look at this finding")}&body=${encodeURIComponent(shareLink)}`;

  const handleCopyTitle = async () => {
    if (title) {
      await copyToClipboard(title);
      setCopied(true);
      setCopyMessage("Title copied");

      setTimeout(() => {
        setCopied(false);
        setShareAnchorEl(null);
      }, 800);
    }
  };

  const handleCopyLink = async () => {
    await copyToClipboard(shareLink);
    setCopied(true);
    setCopyMessage("Link copied");

    setTimeout(() => {
      setCopied(false);
      setShareAnchorEl(null);
    }, 800);
  };

  return (
    <>
      <Tooltip
        title={copied ? copyMessage : "More actions"}
        placement="top"
        arrow
      >
        <div style={{ position: "relative", display: "inline-flex" }}>
          <IconButton
            onClick={(e) => !copied && setShareAnchorEl(e.currentTarget)}
            {...IconButtonProps}
            aria-label="More actions"
            data-test-id="triage-bar-more-actions"
            style={{
              opacity: copied ? 0 : 1,
              transition: "opacity 0.2s ease-in-out",
              position: copied ? "absolute" : "relative",
              zIndex: copied ? 0 : 1,
            }}
          >
            <MoreHoriz />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              opacity: copied ? 1 : 0,
              transition: "opacity 0.2s ease-in-out",
              position: copied ? "relative" : "absolute",
              zIndex: copied ? 1 : 0,
            }}
          >
            <IconButton
              {...IconButtonProps}
              sx={{
                color: (theme) => theme.palette.success.main,
              }}
            >
              <CheckIcon />
            </IconButton>
            <Typography
              variant="caption"
              sx={{
                color: (theme) => theme.palette.success.main,
                fontWeight: "bold",
                position: "absolute",
                top: "100%",
                fontSize: "10px",
                whiteSpace: "nowrap",
              }}
            >
              Copied!
            </Typography>
          </Box>
        </div>
      </Tooltip>
      <Menu
        anchorEl={shareAnchorEl}
        open={shareOpen}
        onClose={() => setShareAnchorEl(null)}
        onClick={() => setShareAnchorEl(null)}
        keepMounted
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {title && (
          <MenuItem onClick={handleCopyTitle}>
            <ListItemIcon>
              <ContentCopy fontSize="inherit" />
            </ListItemIcon>
            <ListItemText>Copy title</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon>
            <Link fontSize="inherit" />
          </ListItemIcon>
          <ListItemText>Copy link</ListItemText>
        </MenuItem>
        <MenuItem component="a" href={shareEmailLink}>
          <ListItemIcon>
            <EmailIcon fontSize="inherit" />
          </ListItemIcon>
          <ListItemText>Email link</ListItemText>
        </MenuItem>
        {additionalMenuItems}
      </Menu>
    </>
  );
}
